/** Ad telemetry only: never changes ad delivery, placement, or the game's wait budgets. */
export const READY_DEADLINE_MS = 4000;
// Reuse the display fallback's existing answer deadline for every network request.
export const AD_ANSWER_TIMEOUT_MS = READY_DEADLINE_MS * 2;
const loaders = new WeakMap();
const consentByWindow = new WeakMap();
const unknown = (value) => typeof value === "boolean" ? value : "unknown";

/** Observe the actual loader, rather than treating our own adsbygoogle queue as loaded. */
export function watchAdLoader(script, win) {
  if (!script || !win) return;
  let state = loaders.get(win);
  if (!state) loaders.set(win, state = { failed: false, scripts: new WeakSet(), listeners: new Set() });
  if (state.scripts.has(script)) return;
  state.scripts.add(script);
  script.addEventListener("error", () => {
    state.failed = true;
    for (const fn of [...state.listeners]) fn();
  });
}

/** Snapshot only public signals. Never store the TC string or change consent settings. */
export function readAdConsent(win = globalThis.window) {
  const tcf = typeof win?.__tcfapi === "function";
  let state = win && consentByWindow.get(win);
  if (tcf && state?.api !== win.__tcfapi) {
    state = { api: win.__tcfapi, data: null, promptSeen: false, answered: false, listeners: new Set() };
    consentByWindow.set(win, state);
    const receive = (data, ok) => {
      if (!ok || !data) return;
      state.data = data;
      state.promptSeen ||= data.eventStatus === "cmpuishown";
      state.answered ||= Boolean(data.tcString) || (typeof data.gdprApplies === "boolean" && ["tcloaded", "useractioncomplete"].includes(data.eventStatus));
      for (const fn of [...state.listeners]) fn();
    };
    try { win.__tcfapi("addEventListener", 2, receive); } catch { /* unsupported CMP */ }
    try { win.__tcfapi("getTCData", 2, receive); } catch { /* TCF 2.2 may only support the listener */ }
  }
  const data = tcf ? state?.data : null;
  const mode = {};
  const layer = Array.isArray(win?.dataLayer) ? win.dataLayer : [];
  // With any regional default on the page (the GA4 bootstrap's EEA/UK/CH denial), a
  // region-less default is only the fallback for *other* regions: which one applies to this
  // visitor cannot be resolved here, so only explicit updates are reported.
  const regional = layer.some((entry) => entry?.[0] === "consent" && entry[1] === "default" && entry[2]?.region);
  // gtag's documented consent default/update commands are queued as Arguments in dataLayer.
  for (const entry of layer) {
    if (entry?.[0] !== "consent" || !["default", "update"].includes(entry[1])) continue;
    // Regional defaults cannot be resolved here; do not claim they apply to this visitor.
    if (entry[2]?.region || (regional && entry[1] === "default")) continue;
    for (const key of ["ad_storage", "ad_user_data", "ad_personalization"]) {
      if (["granted", "denied"].includes(entry[2]?.[key])) mode[key] = entry[2][key];
    }
  }
  const npa = win?.adsbygoogle?.requestNonPersonalizedAds;
  const denied = Object.values(mode).includes("denied");
  return {
    consent_ads: npa === 1 || npa === "1" || denied ? "npa" : npa === 0 || npa === "0" ? "personalized" : "unknown",
    tcf_present: tcf,
    gdpr_applies: unknown(data?.gdprApplies),
    tc_string_present: data ? Boolean(data.tcString) : "unknown",
    tcf_purpose1_consent: unknown(data?.purpose?.consents?.[1]),
    google_npa: npa === 1 || npa === "1" ? "1" : npa === 0 || npa === "0" ? "0" : "unknown",
    google_ad_storage: mode.ad_storage || "unknown",
    google_ad_user_data: mode.ad_user_data || "unknown",
    google_ad_personalization: mode.ad_personalization || "unknown",
  };
}

/** CMP observations, without retaining or sending the TC string. */
export function cmpState(win = globalThis.window) {
  readAdConsent(win);
  const state = win && consentByWindow.get(win);
  return { cmp_loaded: typeof win?.__tcfapi === "function" || Boolean(win?.googlefc),
    cmp_prompt_shown: state?.promptSeen === true || win?.__barumbaCmpPromptSeen === true, cmp_answered: state?.answered === true || win?.__barumbaCmpAnswered === true };
}

/**
 * Telemetry only: is a consent prompt covering the page right now? TCF v2 reports its own UI
 * through the event status it pushes to listeners (`cmpuishown` while the prompt is up,
 * `tcloaded` / `useractioncomplete` once it is not), so this reads the snapshot the listener
 * registered by readAdConsent() already keeps. Returns `null` when the page has no CMP or it
 * has not answered yet: an unknown prompt state is never reported as "no prompt".
 */
export function consentPromptShowing(win = globalThis.window) {
  if (typeof win?.__tcfapi !== "function") return null;
  // Attaches the TCF listener once per window, so later reads see its updates.
  readAdConsent(win);
  const status = consentByWindow.get(win)?.data?.eventStatus;
  if (status === "cmpuishown") return true;
  if (status === "tcloaded" || status === "useractioncomplete") return false;
  return null;
}

/**
 * Telemetry only: is `doc` visible right now? `undefined` when there is no document, so a
 * caller with no window never falls back to another document's state (pass it explicitly).
 */
export function pageVisible(doc) {
  const state = doc?.visibilityState;
  return typeof state === "string" ? state === "visible" : undefined;
}

export function adEventProps(win, props = {}) {
  return { ...props, ...(win?.__barumbaAdStub ? { ad_stub: 1 } : {}) };
}

/** One request, one terminal result. Late/duplicate SDK callbacks cannot rewrite the result. */
export function createAdRequest(tracker, input, { win = globalThis.window, timeoutMs = AD_ANSWER_TIMEOUT_MS, onTerminal } = {}) {
  const props = adEventProps(win, {
    ...input,
    ad_request_id: input.ad_request_id || tracker?.shortId?.("ad") || `ad_${Math.random().toString(36).slice(2, 14)}`,
    ...readAdConsent(win),
  });
  const send = (event, extra = {}) => {
    try { return tracker?.track?.(event, adEventProps(win, { ...props, ...extra })); } catch { /* analytics never breaks ads */ }
  };
  let done = false;
  let timer;
  let offHide;
  let consentTimer;
  let requestSent = false;
  let terminal;
  const consent = consentByWindow.get(win);
  const loader = props.ad_network === "h5_games_ads" ? loaders.get(win) : null;
  const removeHide = () => {
    offHide?.();
    win?.removeEventListener?.("pagehide", hide);
  };
  const sendRequest = () => {
    if (requestSent) return;
    requestSent = true;
    clearTimeout(consentTimer);
    consent?.listeners.delete(sendRequest);
    Object.assign(props, readAdConsent(win));
    send("ad_request");
    if (terminal) {
      send(terminal.event, terminal.extra);
      removeHide();
    }
  };
  const finish = (event, extra = {}) => {
    if (done) return false;
    done = true;
    clearTimeout(timer);
    loader?.listeners.delete(blocked);
    // "No answer" is only readable with the tab state at that moment: a hidden tab is a
    // different story from a visible page the network never answered. Telemetry only.
    if (event === "ad_no_answer") {
      const visible = pageVisible(win?.document);
      if (typeof visible === "boolean") extra = { ...extra, page_visible: visible };
    }
    terminal = { event, extra };
    if (requestSent) {
      send(event, extra);
      removeHide();
    }
    try { onTerminal?.(); } catch { /* cleanup must not affect ad delivery */ }
    return true;
  };
  const blocked = () => finish("ad_blocked", { ad_status: "loader_error" });
  const hide = () => {
    finish("ad_no_answer", { ad_status: "pagehide" });
    sendRequest();
    // Also works when the tracker was not started, or its pagehide listener already ran.
    tracker?.flush?.({ beacon: true });
  };
  if (tracker?.onHide) offHide = tracker.onHide((trigger) => { if (trigger === "pagehide") hide(); });
  else win?.addEventListener?.("pagehide", hide);
  loader?.listeners.add(blocked);
  timer = setTimeout(() => {
    const missing = props.ad_network === "h5_games_ads" && !win?.adsbygoogle;
    finish(missing ? "ad_blocked" : "ad_no_answer", { ad_status: missing ? "missing_global" : "timeout" });
  }, timeoutMs);
  timer.unref?.();
  // Only telemetry waits for the CMP, for at most 150 ms. SDK calls and game budgets do not.
  // A teardown forces the available snapshot out immediately, before the analytics beacon.
  if (props.tcf_present && !consent?.data) {
    consent?.listeners.add(sendRequest);
    consentTimer = setTimeout(sendRequest, 150);
    consentTimer.unref?.();
  } else sendRequest();
  if (loader?.failed) blocked();
  // A ready rewarded offer has answered; the player may wait indefinitely before playback.
  // Keep the request open so only playback (or a later terminal callback) records a result.
  const ready = () => clearTimeout(timer);
  return { props, ready, finish };
}
